import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentJointConfirmInformationData,
  type EnrollmentJointConfirmInformationSubmitInput,
  type EnrollmentJointConfirmInformationSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Joint ConfirmInformation data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointConfirmInformationData>
 * @example const response = await getEnrollmentJointConfirmInformationData()
 */
export const getEnrollmentJointConfirmInformationData = async (
  context?: DefaultContext,
): Promise<EnrollmentJointConfirmInformationData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentJointConfirmInformationData',
      query: `mutation getEnrollmentJointConfirmInformationData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            applicationId
            path
            progress
            eSessionId
            name {
              firstName
              lastName
            }
            phone {
              countryPhoneCode
              phoneNumber
            }
            email
            addressLine1
            addressLine2
            addressCity
            addressRegionCode
            addressPostalCode
            addressCountry
            employmentStatus
            occupation
            isBrokerDealerEmployer
            dateOfBirth

            maritalStatus
            isPoliticallyExposedPerson
            affiliatedPoliticalOrganizations
            investmentObjective
            investmentExperience
            taxFilingStatus
            maskedTaxId
            hasPoliticallyExposedImmediateFamilyMembers
            politicallyExposedImmediateFamilyMembers
            isDirectorPolicyMaker

            personalAnnualGrossIncome
            personalNetWorthRange
            directorPolicyMakerOfCompany
          }
        }
      }`,
    },
    {
      input: {},
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointConfirmInformationData }
  return response.submitJointEnrollment
}

export const useGetEnrollmentJointConfirmInformationData = createQueryHook<
  undefined,
  EnrollmentJointConfirmInformationData
>(getEnrollmentJointConfirmInformationData)

/**
 * Submit Enrollment Joint ConfirmInformation data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointConfirmInformationSubmitOutput>
 * @example const response = await submitEnrollmentJointConfirmInformationData();
 */
export const submitEnrollmentJointConfirmInformationData = async (
  enrollmentConfirmInformationSubmitInput: EnrollmentJointConfirmInformationSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentJointConfirmInformationSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointConfirmInformationData',
      query: `mutation submitEnrollmentJointConfirmInformationData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            applicationId
            path
          }
        }
      }`,
    },
    {
      input: enrollmentConfirmInformationSubmitInput,
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointConfirmInformationSubmitOutput }
  return response.submitJointEnrollment
}

export const useSubmitEnrollmentJointConfirmInformationData = createMutationHook<
  EnrollmentJointConfirmInformationSubmitInput,
  EnrollmentJointConfirmInformationSubmitOutput
>(submitEnrollmentJointConfirmInformationData)

/**
 * Submit Enrollment Joint ConfirmInformation data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointConfirmInformationSubmitOutput>
 * @example const response = await submitEnrollmentJointConfirmInformationData();
 */
export const submitEnrollmentJointConfirmInformationSwitch = async (
  enrollmentConfirmInformationSubmitInput: EnrollmentJointConfirmInformationSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentJointConfirmInformationSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointConfirmInformationData',
      query: `mutation submitEnrollmentJointConfirmInformationData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            path
          }
        }
      }`,
    },
    {
      input: enrollmentConfirmInformationSubmitInput,
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointConfirmInformationSubmitOutput }
  return response.submitJointEnrollment
}

export const useSubmitEnrollmentJointConfirmInformationSwitchData = createMutationHook<
  EnrollmentJointConfirmInformationSubmitInput,
  EnrollmentJointConfirmInformationSubmitOutput
>(submitEnrollmentJointConfirmInformationSwitch)
